import React from "react";
import { ContentSection, HeaderSection } from "../docs/sections";
import { DocPageMeta, Header, Image } from "../docs";
import { Paragraph, TextContainer } from "../../lib/src";
import PatternDesignPrinciples from "../assets/patterns/pattern-design-principles.png";

export const Meta: DocPageMeta = {
	category: "Designing",
};

export default (): JSX.Element => {
	return (
		<>
			<HeaderSection
				title="Design Principles"
				subTitle="Help individual designers and teams stay focused on user needs, prioritize with intent, and avoid making decisions based on personal bias."
			/>
			<ContentSection>
				<TextContainer article>
					<Image src={PatternDesignPrinciples} alt="Summary of design principles" />
					<Header.H3>What are Design Principles?</Header.H3>
					<Paragraph>
						Design Principles are a set of core values and fundamental beliefs from which all design
						decisions flow. They help individual designers and teams stay focused on user needs,
						prioritize with intent, and avoid making decisions based on personal bias. A
						well-crafted set of principles provides a scalable framework for decision-making.
					</Paragraph>
					<Paragraph>
						By focusing on these six Design Principles, product teams are able to make user-centered
						decisions throughout the design and delivery process that move us toward preferred
						outcomes and deliver a user experience that is unmistakably Siteimprove.
					</Paragraph>
					<Header.H3>Accessibility, then aesthetics</Header.H3>
					<Paragraph>
						Siteimprove is an accessibility thought leader, a community and legislative participant,
						and an accessibility tool provider. To uphold our company-wide accessibility pledge,
						every product and design choice needs to take accessibility into consideration. It isn't
						about sacrificing aesthetics, but about prioritizing usability and value over aesthetics
						alone.
					</Paragraph>
					<Header.H3>Delight with speed and simplicity</Header.H3>
					<Paragraph>
						Our users are busy, wear many hats, and switch contexts often, so we provide them with
						what they need and get out of the way. By prioritizing simplicity in design and
						optimizing performance, we provide delightful interactions that quickly fulfill user
						needs, leading to increased satisfaction and engagement.
					</Paragraph>
					<Header.H3>Highlight the essential, enable the intricate</Header.H3>
					<Paragraph>
						Our users come from different backgrounds and have different levels of experience within
						their fields. We understand the importance of making important actions and information
						easily visible, while also allowing for flexibility when it comes to handling intricate
						tasks. Our goal is to make sure users can quickly find the most important tools, while
						also providing advanced features for dealing with complex situations.
					</Paragraph>
					<Header.H3>Make the complex beautifully clear</Header.H3>
					<Paragraph>
						Many of our users are not data experts and need guidance when working with it. By
						transforming complicated, intricate information into a visually clear and engaging
						format, we aim to enhance comprehension, facilitate decision-making, and provide users
						with a positive experience when interacting with data-focused features and products.
					</Paragraph>
					<Header.H3>Connect workflows to unite teams</Header.H3>
					<Paragraph>
						Fragmented teams and siloed contributors impede the success of marketing organizations.
						To overcome this challenge, team members must unite to prioritize and execute tasks with
						the same goal in mind. When workflows are connected and data is unified, insights become
						more valuable, and cross-domain visibility is enhanced. This provides everyone on the
						team with what they need to perform at their best and achieve success together.
					</Paragraph>
					<Header.H3>Provide paths to mastery</Header.H3>
					<Paragraph>
						We are dedicated to empowering marketers of all levels to succeed, from those at the
						beginning of their digital marketing journey to seasoned professionals looking to refine
						their strategies. We simplify the basics for beginners and gradually introduce
						additional capabilities as they become more skilled and confident. This approach
						empowers digital marketers to achieve remarkable things with our product and become
						masters in their field.
					</Paragraph>
					<Header.H3>Learn more</Header.H3>
					<Paragraph>
						If you are curious about how these principles were created or would like some examples
						of how to apply them, take a look at{" "}
						<a
							href="https://sintra.siteimprove.com/home/siteimproves-product-design-principles"
							target="_blank"
							rel="noreferrer"
						>
							this page{" "}
						</a>{" "}
						discussing the principles in more detail. You are also welcome to reach out to the
						Design Team for more information on how we use Design Principles during the product
						design process.
					</Paragraph>
				</TextContainer>
			</ContentSection>
		</>
	);
};
